// Event-Marker (Elastic Audio): erkannte Anschläge einer Audiodatei, einmal je Quelle berechnet und gemerkt.
import type { Clip, Project } from '@srs/shared';
import { detectOnsets } from '../edit/beat.ts';
import { clipMap } from '../edit/warp.ts';
import { engine } from './engine.ts';

const cache = new Map<string, number[]>();
const busy = new Set<string>();
/** Anschläge der Quelle in Quell-Sekunden (stärkere zuerst gefiltert); null, solange noch gerechnet wird */
export function eventsOf(sourceId: string): number[] | null {
  const have = cache.get(sourceId);
  if (have) return have;
  if (!busy.has(sourceId)) {
    busy.add(sourceId);
    setTimeout(() => {
      compute(sourceId);
      busy.delete(sourceId);
    }, 30);
  }
  return null;
}
function compute(sourceId: string): number[] | null {
  const b = engine.buffers.get(sourceId);
  if (!b) return null;
  const chans = Array.from({ length: b.numberOfChannels }, (_, i) => b.getChannelData(i));
  const raw = detectOnsets(chans, b.sampleRate, 0, b.duration, 'enhanced');
  const max = Math.max(1e-9, ...raw.map((o) => o.s));
  const list = raw.filter((o) => o.s / max >= 0.3).map((o) => o.t);
  cache.set(sourceId, list);
  return list;
}
/** Wie eventsOf, rechnet aber sofort (für Befehle, die das Ergebnis jetzt brauchen) */
export function eventsNow(sourceId: string): number[] | null {
  return cache.get(sourceId) ?? compute(sourceId);
}
/** Anschläge eines Audio-Clips in Sekunden ab Clip-Anfang (nur innerhalb des Clips); null, solange noch gerechnet wird */
export function clipTransients(p: Project, c: Clip, now = false): number[] | null {
  if (c.kind !== 'audio' || !c.sourceId) return [];
  const ev = now ? eventsNow(c.sourceId) : eventsOf(c.sourceId);
  if (!ev) return null;
  const m = clipMap(p, c), out: number[] = [];
  for (const s of ev) { const t = m.toTime(s) - c.start; if (t >= -1e-4 && t <= c.duration + 1e-4) out.push(t); }
  return out;
}
